import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Coredata, Service } from 'webapp-types';
import { AccountType, BillingFeature, UserLevel } from 'webapp/enums/enums';
import { deleteKeys } from 'webapp/utils/common';

import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import DataLoaderBase from 'webapp/data-loader/base';

import { AppType as VersionCheckerAppType } from 'webapp/services/version-checker';

export default class DataLoaderDatawall extends DataLoaderBase {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('connect') declare connectService: Service.Connect;

	initialRequestsPromise = Promise.resolve();

	@tracked isDatawallExpired = false;
	@tracked isDatawallInactive = false;
	@tracked isDatawallPasswordProtected = false;
	@tracked showDatawallLock = false;
	@tracked spaceId: number | null = null; // used ito mark the space the shown object is from
	@tracked hasAccessToBoard = true; // used to determine if previews are allowed (together with allowAnonymousDateRangeEdit/allowAnonymousFilterEdit), set by BE.

	loadGlobals(allData: Coredata.Datawall): void {
		// create temporary user record (for current-user service)
		const tmpUser = this.mmUsersService.createNewModel({
			id: allData.userId,
			level: allData.userLevel as UserLevel, // null for anonymous viewers
		});

		this.currentUserService.model = tmpUser;
		this.currentUserService.loggedInAsPowerUser = allData.loggedInAsPowerUser;
		this.currentUserService.isPowerUser = allData.isPowerUser;
		this.currentUserService.isPlatformAdmin = allData.isPlatformAdmin;

		deleteKeys(
			allData,
			'userId',
			'userLevel',
			'loggedInAsPowerUser',
			'isPowerUser',
			'isPlatformAdmin',
		);

		// save tokens
		this.currentUserService.userDataToken = allData.userDataToken;
		this.currentUserService.accessToken = allData.accessToken;
		this.currentUserService.accessTokenExpiry = allData.accessTokenExpiry;
		this.currentUserService.initRefreshingTokens();

		deleteKeys(allData, 'userDataToken', 'accessToken', 'accessTokenExpiry');

		this.isDatawallExpired = allData.isExpired;
		this.isDatawallInactive = allData.isInactive;
		this.isDatawallPasswordProtected = allData.isPasswordProtected;
		this.showDatawallLock = allData.showLock;

		deleteKeys(allData, 'isExpired', 'isInactive', 'isPasswordProtected', 'showLock');

		this.billingService.updateFeatureInfo({
			[BillingFeature.WHITE_LABELING]: !!allData.isWhiteLabelingEnabled,
			// BE still sends hideDatawallBranding, but the server stopped passing it on in #7012:
			// removeBrandingOnFeatures controls the branding instead
			[BillingFeature.SKIP_DW_BRANDING]: false,
		});

		deleteKeys(allData, 'isWhiteLabelingEnabled');

		this.mmDataPerformanceSummariesService.limits.forEach((limit) => {
			limit.remaining = Infinity; // Let the first call to summaries set the real limits, on init allow infinite
		});
		this.mmDataPerformanceSummariesService.limits =
			this.mmDataPerformanceSummariesService.limits;

		this.accountService.id = Number(allData.spaceId);
		if (allData.isClient) {
			this.accountService.accountType = AccountType.CLIENT; // used for manual sync
		}
		deleteKeys(allData, 'spaceId', 'isClient');

		this.accountService.removeBrandingOnFeatures = !!allData.removeBrandingOnFeatures;
		deleteKeys(allData, 'removeBrandingOnFeatures');

		this.accountService.updateWhitelabelData(allData.whiteLabeling);
		deleteKeys(allData, 'whiteLabeling');

		this.requestService.apiUrl = allData.apiUrl;
		this.requestService.debugParam = allData.debugParam;
		this.requestService.pusherEnvId = allData.pusherEnvId;
		this.requestService.pusherKey = allData.pusherKey;
		this.requestService.pusherCluster = allData.pusherCluster;
		this.requestService.wallToken = allData.wallToken;

		deleteKeys(
			allData,
			'apiUrl',
			'debugParam',
			'pusherEnvId',
			'pusherKey',
			'pusherCluster',
			'wallToken',
		);

		const { boards, boardLoop, report } = allData as APIData.App.DatawallRecords;
		this.storeService.pushPayload({ boards });
		if (boardLoop) {
			this.storeService.pushPayload({ boardLoop });
		}
		if (report) {
			this.storeService.pushPayload({ report });
		}
		deleteKeys(allData, 'boards', 'boardLoop', 'report');

		this.versionCheckerService.version = allData.version;
		this.versionCheckerService.appType = VersionCheckerAppType.DATAWALL;

		deleteKeys(allData, 'version');

		this.mmBoardsService.maxBoardIdsToRemember = Infinity;

		// cdr
		DateRange.setFormulas(allData);

		DateRange.createPredefinedRanges();
		DateRange.createPredefinedGranulations();
		DsCompare.createPredefinedCompares();

		deleteKeys(allData, 'cdrRanges', 'cdrGranulations');

		MetricLimitations.parseBaseConstraints(allData.cdrLimitationsBase.ranges);

		deleteKeys(allData, 'cdrLimitationsBase');

		// we have local config available for board colors
		deleteKeys(allData, 'boardColors');

		// all the rest: constants
		const constants = allData;

		this.constantsService.update(constants);

		// analytics
		this.analyticsService.initializeFromEnv();

		// account service
		this.accountService.accountServiceUrl = allData.accountServiceUrl;
		deleteKeys(allData, 'accountServiceUrl');

		this.requestService.websiteUrl = allData.websiteUrl;
		deleteKeys(allData, 'websiteUrl');

		this.connectService.initialize();
	}

	doInitialRequests(): Promise<void> {
		return Promise.resolve();
	}

	afterInitialRequests(_initialRouteTo: string): Promise<void> {
		return Promise.resolve();
	}
}
